import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { ApiError, useApiClient, type ApiClient, type components, type Panel } from '../api';

export type Role = 'owner' | 'staff' | 'admin' | 'support';
export type Principal = components['schemas']['Me'] | components['schemas']['OperatorMe'];
export type Session = {
  principal: Principal | null;
  loading: boolean;
  isOwner: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  isSupport: boolean;
};
// What login(), logout() and the route guard work with: the app's API client and query cache.
export type AuthDeps = { api: ApiClient; queryClient: QueryClient };

const mePaths = { merchant: '/api/me', admin: '/admin/api/me' } as const;

// The library's key of the panel's GET /me: the query that holds the principal (UI architecture §6).
export function sessionQueryKey(panel: Panel) {
  return ['get', mePaths[panel]] as const;
}

// GET /me of the panel.
export async function readMe(api: ApiClient): Promise<Principal> {
  const { data } =
    api.panel === 'merchant'
      ? await api.fetchClient.GET('/api/me')
      : await api.fetchClient.GET('/admin/api/me');
  // The client rejects every non-2xx answer with an ApiError, so a returned call has its data.
  return data!;
}

// /me runs on start-up and after login, then only when a screen invalidates it (merchant screens
// §3.1). A 401 leaves the session null: on start-up it is "not logged in" (UI architecture §6);
// with a principal cached the session was lost, and like any 401 it redirects to /login?returnTo=
// (merchant screens §3.1; admin screens §3.1).
export function sessionQuery(api: ApiClient) {
  const queryKey = sessionQueryKey(api.panel);
  return queryOptions({
    queryKey,
    queryFn: async ({ client }): Promise<Principal | null> => {
      try {
        return await readMe(api);
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) throw error;
        if (client.getQueryData(queryKey)) api.onUnauthenticated();
        return null;
      }
    },
    staleTime: Infinity,
  });
}

export function useSession(): Session {
  const api = useApiClient();
  const { data, isPending } = useQuery(sessionQuery(api));
  const principal = data ?? null;
  return {
    principal,
    loading: isPending,
    isOwner: principal?.role === 'owner',
    isStaff: principal?.role === 'staff',
    isAdmin: principal?.role === 'admin',
    isSupport: principal?.role === 'support',
  };
}
